import { ThemeProvider } from "@/components/theme-provider"
import { Toaster } from "@/components/ui/toaster"
import { useToast } from "@/components/ui/use-toast"
import { mutationCache, queryCache, queryClient } from "@/lib/react-query"
import { useIsFetching, useIsMutating } from "@tanstack/react-query"
import { Link, Outlet } from "@tanstack/react-router"
import { useEffect, useRef } from "react"
import LoadingBar, { LoadingBarRef } from "react-top-loading-bar"
import { appName } from "../../../api/src/lib/utilsWithoutImports"
import { captureError } from "@/lib/log"

function LoaderBar() {
  const isFetching = useIsFetching()
  const isMutating = useIsMutating()
  const ref = useRef<LoadingBarRef>(null)

  useEffect(() => {
    if (isFetching || isMutating) {
      ref.current?.continuousStart()
    } else {
      ref.current?.complete()
    }
  }, [isFetching, isMutating])

  return (
    <LoadingBar
      className="!bg-pillarscream"
      height={4}
      ref={ref}
      shadow={false}
    />
  )
}

export function Root() {
  const { toast } = useToast()

  useEffect(() => {
    mutationCache.config.onError = (error: any) => {
      captureError(error, { tags: { source: "mutation" } })
      const title = error.body?.message ?? "Unknown error"
      toast({ title, variant: "destructive" })
    }
    queryCache.config.onError = (error: any) => {
      captureError(error, { tags: { source: "query" } })
      const title = error.body?.message ?? "Unknown error"
      toast({ title, variant: "destructive" })
    }
  }, [toast])

  const firstRender = useRef(true)
  useEffect(() => {
    if (import.meta.env.VITE_APP_ENV === "development" && firstRender.current) {
      document.title = "[DEV] " + document.title
      firstRender.current = false
    }
  }, [])

  return (
    <>
      <LoaderBar />
      <Toaster />
      <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
        <div className="!text-gray-500 h-full">
          <Outlet />
        </div>
      </ThemeProvider>
    </>
  )
}
